// Analytics data layer — pure fetch, no React. The browser calls OUR OWN server
// route (`/api/meetings/:id/analytics`), which attaches the tenant API key
// server-side and forwards to the NSI Meet backend. The key is NEVER sent from
// or exposed to the browser (analytics prompt, security constraint).

import { http } from '@/services/http/client';
import type { RequestOptions } from '@/services/http/client';
import type {
  AnalyticsMeetingType,
  AnalyticsStatus,
  Attendee,
  AttendeeRole,
  MeetingAnalytics,
  RecordingMode,
  RecordingStatus,
} from './types';

type Raw = Record<string, unknown>;

/** First defined non-empty string among several key spellings, else null. */
function str(raw: Raw, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/** Finite number among several key spellings, else 0 (zero is a real value). */
function num(raw: Raw, ...keys: string[]): number {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

function bool(raw: Raw, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'boolean') return v;
  }
  return false;
}

function asStatus(v: string | null): AnalyticsStatus | null {
  return v === 'scheduled' || v === 'live' || v === 'ended' ? v : null;
}

function asMeetingType(v: string | null): AnalyticsMeetingType | null {
  return v === 'instant' || v === 'scheduled' ? v : null;
}

function asRole(v: string | null): AttendeeRole {
  // Attendees are one of two roles; default to 'lead' on anything unexpected so
  // a row still renders rather than crashing on an unknown enum.
  return v === 'salesperson' ? 'salesperson' : 'lead';
}

function asRecordingStatus(v: string | null): RecordingStatus {
  return v === 'recording' || v === 'processing' || v === 'completed' || v === 'failed'
    ? v
    : 'none';
}

function asRecordingMode(v: string | null): RecordingMode {
  return v === 'audio_video' ? 'audio_video' : 'audio_only';
}

function normalizeAttendance(raw: Raw): Attendee[] {
  const list = raw.attendance;
  if (!Array.isArray(list)) return [];
  const out: Attendee[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Raw;
    out.push({
      identity: str(o, 'identity') ?? '',
      role: asRole(str(o, 'role')),
      presentSeconds: num(o, 'present_seconds', 'presentSeconds'),
      stillJoined: bool(o, 'still_joined', 'stillJoined'),
    });
  }
  return out;
}

/** Map the backend record (snake_case, camelCase tolerated) to the UI model. */
function normalizeAnalytics(raw: Raw): MeetingAnalytics {
  return {
    meetingId: str(raw, 'meeting_id', 'meetingId') ?? '',
    status: asStatus(str(raw, 'status')),
    meetingType: asMeetingType(str(raw, 'meeting_type', 'meetingType')),
    salespersonName: str(raw, 'salesperson_name', 'salespersonName') ?? '',
    leadName: str(raw, 'lead_name', 'leadName') ?? '',
    outcome: str(raw, 'outcome'),
    notesText: str(raw, 'notes_text', 'notesText'),
    notesSubmitted: bool(raw, 'notes_submitted', 'notesSubmitted'),
    nextStepDate: str(raw, 'next_step_date', 'nextStepDate'),
    scheduledStart: str(raw, 'scheduled_start', 'scheduledStart'),
    scheduledEnd: str(raw, 'scheduled_end', 'scheduledEnd'),
    actualStart: str(raw, 'actual_start', 'actualStart'),
    actualEnd: str(raw, 'actual_end', 'actualEnd'),
    durationSeconds: num(raw, 'duration_seconds', 'durationSeconds'),
    attendance: normalizeAttendance(raw),
    recordingStatus: asRecordingStatus(str(raw, 'recording_status', 'recordingStatus')),
    recordingDurationSeconds: num(raw, 'recording_duration_seconds', 'recordingDurationSeconds'),
    recordingMode: asRecordingMode(str(raw, 'recording_mode', 'recordingMode')),
    recordingUrl: str(raw, 'recording_url', 'recordingUrl'),
  };
}

export const analyticsApi = {
  /** GET the per-meeting analytics via our key-attaching server proxy. */
  async get(meetingId: string, opts?: RequestOptions): Promise<MeetingAnalytics> {
    const raw = await http.get<Raw>(
      `/api/meetings/${encodeURIComponent(meetingId)}/analytics`,
      opts,
    );
    return normalizeAnalytics(raw);
  },

  /**
   * Same-origin URL for the recording bytes. The `<audio>`/download point HERE,
   * not at the backend `recording_url` — only this route can attach the API key
   * and stream the audio back (a bare backend URL can't carry an Authorization
   * header and would 401).
   */
  recordingProxyUrl(meetingId: string): string {
    return `/api/meetings/${encodeURIComponent(meetingId)}/recording`;
  },
};
